import { UserType } from '../constants/constants';
import { useManageMatterRequestContext } from '../context/ManageMatterRequest';
import { useUser } from '../context/UserContext';
import {
  canEditMatterRequestPeople,
  isUserInGroup,
} from '../helpers/matterRequestPermissionsHelper';
import { ParameterEntityType } from '../interface/common';
import { IParameters } from '../interface/parameters';
import { useFetchUserParameters } from './useFetchUserParameters';

interface UseMatterRequestEditPermissionsOptions {
  isEditing: boolean;
}

/**
 * Rights come from the saved record, not the working copy, so reassigning the owner in the form
 * doesn't take away the editing rights of the user who made the change.
 */
export const useMatterRequestEditPermissions = ({
  isEditing,
}: UseMatterRequestEditPermissionsOptions) => {
  const { user } = useUser();
  const { initialManageMatterRequest } = useManageMatterRequestContext();

  const permissionParams = {
    matterRequest: initialManageMatterRequest ?? undefined,
    userId: user?.id,
    userType: user?.userType,
  };
  const canEditPeopleWithoutParameter = canEditMatterRequestPeople({
    ...permissionParams,
    assistantMayModifyUserRights: false,
    isExtendedRightsGroupMember: false,
  });

  const { data } = useFetchUserParameters({
    parametersToGet: ParameterEntityType.MATTER_SECURITY,
    enabled: isEditing && !canEditPeopleWithoutParameter && user?.userType !== UserType.RESTRICTED,
  });
  const matterSecurityParameters = (data as IParameters | undefined)?.parameters
    ?.matterSecurityParameters;
  const assistantMayModifyUserRights = Boolean(
    matterSecurityParameters?.allowMatterAssistantToModifyMatterUserRights,
  );
  const isExtendedRightsGroupMember = isUserInGroup(
    user?.userGroups,
    matterSecurityParameters?.extendedRightsForMatterAndPreliminaryInvoiceUserGroupId,
  );

  if (!isEditing) return { isPeopleReadOnly: false };

  return {
    isPeopleReadOnly: !canEditMatterRequestPeople({
      ...permissionParams,
      assistantMayModifyUserRights,
      isExtendedRightsGroupMember,
    }),
  };
};
